import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch, useSelector } from 'react-redux';
import * as action from '../Store/Pom.action';
import { getSubBrandAccess, getPCTypesDetails, getCatBasedOnPT } from '../../../SystemMasters/Store/SystemMasters.action';
import { checkArray, createOptions } from '../../../../Components/helper';
import { trimGradingDecimals } from '../../../../utils/calculationHelpers';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { pomRowKey, resolvePomDeletion } from './pomRowIdentity';

export const StateContext = createContext(null);

export default function AddGradingGroupProvider({ children }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation('common');
  const history = useHistory();
  const isView = window.location?.search;
  const reducerState = useSelector((redState) => redState);
  const {
    PomDetails: {
      BrandList,
      CategoryOneList,
      CategoryTwoList,
      CategoryThreeList,
      pomList,
      GradingDataById,
      UnitOfMeasure,
      ecommAcess,
      eCommSpecData,
      foundReferences,
      ProfileData,
      updatedExcelPOMData,
      pomGroupList,
    },
    UserDetails: { userInfo },
    Users: { logInRights },
    SystemMastersDetails: { subBrandAccess, multiSubBrandList, pcTypeList },
  } = reducerState;
  const loggedInUserId = userInfo.userId;
  const { CheckActionPermission } = useComponent();
  const GradingAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'pomGrading', 'addEdit');
  const GradingDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'pomGrading', 'delete');

  const initialState = {
    productTypeData: [],
    productTypeId: [],
    groupName: '',
    groupDescription: '',
    BrandData: [],
    selectGradingBrand: [],
    selectSubBrand: [],
    CategoryOneData: [],
    selectCategoryOne: [],
    CategoryTwoData: [],
    selectCategoryTwo: [],
    CategoryThreeData: [],
    selectCategoryThree: [],
    sizeRangeId: null,
    libraryList: [],
    pomTableData: [],
    sizeRanges: [
      { name: '', isSampleSize: true },
      { name: '', isSampleSize: false },
      { name: '', isSampleSize: false },
    ],
    sizeRangesForHeader: [
      { sizeName: '', isSampleSize: true },
      { sizeName: '', isSampleSize: false },
      { sizeName: '', isSampleSize: false },
    ],
    applySizeRangesToHeaderText: false,
    selectedPOM: [],
    gradings: [
      {
        sizeName: '',
        grading: '',
        isZero: false,
      },
    ],
    showError: false,
    active: false,
    isSeasonFocused: false,
    isView: false,
    conversationType: '',
    selectedEcommOptions: {},
    sampleSize: [],
    isDelete: true,
    isFocused: false,
    magnified: false,
    showApplyConfirm: false,
    showGradingScopeConfirm: false,
    pendingScopeSubmitPoms: null,
    importModal: false,
    errorLog: [],
    isErrorShow: false,
    conversionTypeId: '',
    groupsList: [],
    isAdd: false,
    isSubBrandAccess: false,
    subBrandOptions: [],
    catBasedOnPTData: false,
    refBrands: [],
    refSubBrands: [],
    refCatsOne: [],
    refCatsTwo: [],
    refCatThree: [],
    refProductTypes: [],
    isLoading: false,
    editModeLoading: !!id,
  };
  const [gradingGroupState, setgradingGroup] = useState({ ...initialState });
  const pomTableDataRef = useRef(gradingGroupState.pomTableData);
  pomTableDataRef.current = gradingGroupState.pomTableData;

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setgradingGroup((prev) => ({
          ...prev,
          catBasedOnPTData: !!res,
          isLoading: false,
        }));
      } catch (e) {
        setgradingGroup((prev) => ({
          ...prev,
          catBasedOnPTData: false,
          isLoading: false,
        }));
      }
    };
    setgradingGroup((prev) => ({
      ...prev,
      isLoading: true,
    }));
    fetchCatBasedOnPTData();
  }, [dispatch]);
  useEffect(() => {
    try {
      dispatch(
        getPCTypesDetails({
          loggedInUserId: userInfo?.userId,
          isActive: true,
          tier: 'Tier1',
        }),
      );
    } catch (e) {
      // no-op
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    setgradingGroup((prev) => ({
      ...prev,
      productTypeData: createOptions(pcTypeList, 'name', '_id'),
    }));
  }, [pcTypeList]);

  // Cascading logic gated by catBasedOnPTData
  useEffect(() => {
    if (!gradingGroupState.catBasedOnPTData) {
      // Flag OFF: preserve legacy behavior (full lists)
      setgradingGroup((prev) => ({
        ...prev,
        CategoryOneData: createOptions(CategoryOneList, 'name', '_id'),
        CategoryTwoData: createOptions(CategoryTwoList, 'name', '_id'),
        CategoryThreeData: createOptions(CategoryThreeList, 'name', '_id'),
      }));
      return;
    }

    const selectedPTIds = Array.isArray(gradingGroupState.productTypeId) ? gradingGroupState.productTypeId : [];
    const selectedC1Ids = Array.isArray(gradingGroupState.selectCategoryOne) ? gradingGroupState.selectCategoryOne : [];
    const selectedC2Ids = Array.isArray(gradingGroupState.selectCategoryTwo) ? gradingGroupState.selectCategoryTwo : [];

    // Cat1: empty until PT selected; then filter by productType relation on Cat1 items
    const cat1Base = CategoryOneList || [];
    const cat1Filtered = !selectedPTIds.length
      ? []
      : cat1Base.filter((c1) => {
          const pts = c1?.productType || [];
          const arr = Array.isArray(pts) ? pts : [pts];
          const ids = arr.map((x) => x?._id || x).filter(Boolean);
          return ids.some((id) => selectedPTIds.includes(id));
        });

    // Cat2: empty until Cat1 selected; then filter by Cat1 relation
    const cat2Base = CategoryTwoList || [];
    const cat2Filtered = !selectedC1Ids.length
      ? []
      : cat2Base.filter((c2) => {
          const rel = c2?.categoryOneId || c2?.catOneIds || [];
          const arr = Array.isArray(rel) ? rel : [rel];
          const ids = arr.map((x) => x?._id || x).filter(Boolean);
          return ids.some((id) => selectedC1Ids.includes(id));
        });

    // Cat3: empty until Cat1+Cat2 selected; then filter by both
    const cat3Base = CategoryThreeList || [];
    const cat3Filtered =
      !selectedC1Ids.length || !selectedC2Ids.length
        ? []
        : cat3Base.filter((c3) => {
            const r1 = c3?.categoryOneId || c3?.catOneIds || [];
            const r2 = c3?.categoryTwoId || c3?.catTwoIds || [];
            const a1 = (Array.isArray(r1) ? r1 : [r1]).map((x) => x?._id || x).filter(Boolean);
            const a2 = (Array.isArray(r2) ? r2 : [r2]).map((x) => x?._id || x).filter(Boolean);
            return a1.some((id) => selectedC1Ids.includes(id)) && a2.some((id) => selectedC2Ids.includes(id));
          });

    setgradingGroup((prev) => ({
      ...prev,
      CategoryOneData: createOptions(cat1Filtered, 'name', '_id'),
      CategoryTwoData: createOptions(cat2Filtered, 'name', '_id'),
      CategoryThreeData: createOptions(cat3Filtered, 'name', '_id'),
    }));
  }, [
    gradingGroupState.catBasedOnPTData,
    gradingGroupState.productTypeId,
    gradingGroupState.selectCategoryOne,
    gradingGroupState.selectCategoryTwo,
    CategoryOneList,
    CategoryTwoList,
    CategoryThreeList,
  ]);

  // Clear dependent selections when parent is removed (flag ON only)
  useEffect(() => {
    if (!gradingGroupState.catBasedOnPTData) return;
    if (!gradingGroupState.productTypeId || gradingGroupState.productTypeId.length === 0) {
      setgradingGroup((prev) => ({
        ...prev,
        selectCategoryOne: [],
        selectCategoryTwo: [],
        selectCategoryThree: [],
      }));
    }
  }, [gradingGroupState.catBasedOnPTData, gradingGroupState.productTypeId]);

  useEffect(() => {
    if (!gradingGroupState.catBasedOnPTData) return;
    if (!gradingGroupState.selectCategoryOne || gradingGroupState.selectCategoryOne.length === 0) {
      setgradingGroup((prev) => ({
        ...prev,
        selectCategoryTwo: [],
        selectCategoryThree: [],
      }));
    }
  }, [gradingGroupState.catBasedOnPTData, gradingGroupState.selectCategoryOne]);

  useEffect(() => {
    if (!gradingGroupState.catBasedOnPTData) return;
    if (!gradingGroupState.selectCategoryTwo || gradingGroupState.selectCategoryTwo.length === 0) {
      setgradingGroup((prev) => ({
        ...prev,
        selectCategoryThree: [],
      }));
    }
  }, [gradingGroupState.catBasedOnPTData, gradingGroupState.selectCategoryTwo]);

  useEffect(() => {
    setgradingGroup((prev) => ({
      ...prev,
      libraryList: pomList,
      groupsList: pomGroupList,
    }));
  }, [pomList, pomGroupList]);

  useEffect(() => {
    setgradingGroup((prev) => ({
      ...prev,
      isSubBrandAccess: subBrandAccess,
    }));
  }, [subBrandAccess]);

  useEffect(() => {
    if (multiSubBrandList?.length > 0) {
      setgradingGroup((prev) => ({
        ...prev,
        subBrandOptions: createOptions(multiSubBrandList, 'name', '_id'),
      }));
    }
  }, [multiSubBrandList]);

  useEffect(() => {
    if (updatedExcelPOMData?.length > 0) {
      setgradingGroup((prev) => {
        const existingItemsMap = new Map(prev.pomTableData.map((item) => [item._id, item]));
        const updatedAndNewItems = [];

        updatedExcelPOMData.forEach((excelItem) => {
          const existingItem = existingItemsMap.get(excelItem._id);

          const newGradings = prev.sizeRangesForHeader?.map((headerItem) => {
            const gradingItem = excelItem?.gradings?.find((grading) => headerItem?.sizeName.toLowerCase() === grading?.sizeName.toLowerCase());
            return {
              sizeName: headerItem?.sizeName,
              isSampleSize: headerItem?.isSampleSize,
              grading: gradingItem?.grading ? gradingItem?.grading : 0,
            };
          });

          if (existingItem) {
            updatedAndNewItems.push({
              ...existingItem,
              ...excelItem,
              annotatedImage: existingItem.annotatedImage,
              annotatedImageKey: existingItem.annotatedImageKey,
              fullImage: existingItem.fullImage,
              fullImageKey: existingItem.fullImageKey,
              imageKey: existingItem.imageKey,
              image: existingItem.image,
              gradings: newGradings,
            });
            existingItemsMap.delete(excelItem._id);
          } else {
            updatedAndNewItems.push({
              ...excelItem,
              gradings: newGradings,
            });
          }
        });

        const remainingItems = Array.from(existingItemsMap.values());
        const finalPomTableData = [...updatedAndNewItems, ...remainingItems];

        return {
          ...prev,
          pomTableData: finalPomTableData,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedExcelPOMData]);

  useEffect(() => {
    const fn = async () => {
      const params = {
        loggedInUserId,
      };
      if (localStorage.getItem('gradingGroupPomIds')) {
        const selectedPomLibraryIds = JSON.parse(localStorage.getItem('gradingGroupPomIds'));
        const res = await dispatch(action.getAllPom(params, t));

        if (res?.data) {
          const tableData = res?.data?.data?.filter((item) => selectedPomLibraryIds.includes(item._id));

          if (tableData) {
            const addGrading = gradingGroupState?.sizeRangesForHeader?.map((item) => ({
              ...item,
              grading: 0,
            }));
            const updatedPomLibraries = tableData.map((library) => {
              const data = { ...library };
              data.gradings = addGrading;
              data.tolPlus = 0;
              data.tolMinus = 0;
              return data;
            });
            setgradingGroup((prev) => ({
              ...prev,
              pomTableData: updatedPomLibraries,
            }));
          }
        }
      }
      localStorage.removeItem('gradingGroupPomIds');
    };
    if (!id) {
      fn();
    }
  }, []);

  useEffect(() => {
    const fetchPurcahseOrder = async () => {
      const queryParams = {
        forGrading: true,
        gradingsGroupId: '',
        isActive: 1,
        loggedInUserId,
      };
      await Promise.all([
        dispatch(action.fetchBrandsForGradingGroup(queryParams)),
        dispatch(getSubBrandAccess()),
        dispatch(action.fetchCategoryOneForGrading({ ...queryParams, categoryType: 1 })),
        dispatch(action.fetchCategoryTwoForGrading({ ...queryParams, categoryType: 2 })),
        dispatch(action.eCommSpecAccess({ key: 'eCommSpec' })),
        dispatch(action.fetcheCommSpecData({ loggedInUserId, isActive: true })),
        dispatch(action.fetchUserProfile()),
        dispatch(
          action.fetchUnitOfMeasure({
            loggedInUserId,
          }),
        ),
      ]);
    };
    fetchPurcahseOrder();
  }, [dispatch, loggedInUserId]);

  useEffect(() => {
    const fetchGradingById = async () => {
      const params = {
        isPubParam: 1,
        loggedInUserId,
        fromDetails: !!gradingGroupState?.catBasedOnPTData,
      };
      await dispatch(action.fetchGradingGroupById(id, params));
      await dispatch(action.checkGradingReference(id));
    };
    if (id) {
      fetchGradingById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, gradingGroupState?.catBasedOnPTData]);

  const isDecimal = (number) => {
    return number % 1 !== 0;
  };

  /**
   * Normalises a tolerance loaded from the document. The decimals it was saved with are kept
   * rather than padded to three, so the value the user typed is the value they get back
   * (BM-9884). A whole tolerance is returned untouched.
   * @param {string|number} item stored tolerance
   * @returns {string|number}
   */
  const checkNumber = (item) => {
    if (isDecimal(item)) {
      return trimGradingDecimals(item);
    }
    return item;
  };

  /**
   * Normalises a decimal grade rule loaded from the document, keeping the decimals it was
   * written with instead of padding it out to three places (BM-9884).
   * @param {string|number} value stored grade rule
   * @returns {string|number} the trimmed rule, or NaN when the value is not numeric
   */
  const conditionForDecimal = (value) => {
    const numericValue = parseFloat(value);
    if (!Number.isNaN(numericValue)) {
      return trimGradingDecimals(value);
    }
    return numericValue;
  };

  const conditionForInteger = (value) => {
    const numericValue = parseFloat(value);
    if (Number.isInteger(numericValue)) {
      // Preserve the original "+" sign if it was present
      const hasExplicitPlus = String(value).trim().startsWith('+');
      return hasExplicitPlus && numericValue > 0 ? `+${numericValue}` : numericValue.toString();
    }

    return value;
  };

  const applyToDecimalFunctionality = () => {
    return GradingDataById?.pomLibraries?.map((item) => {
      const newGradings = item?.gradings?.map((grading) => {
        let gradingValue;
        if (!Number.isNaN(grading?.grading) && !Number.isNaN(parseFloat(grading?.grading))) {
          gradingValue = isDecimal(grading?.grading) ? conditionForDecimal(grading?.grading) : conditionForInteger(grading?.grading);
        } else {
          gradingValue = grading?.grading || 0;
        }

        return {
          ...grading,
          grading: gradingValue,
        };
      });
      const tolPlusValue = item?.tolPlus ? checkNumber(item?.tolPlus) : 0;
      const tolMinusValue = item?.tolMinus ? checkNumber(item?.tolMinus) : 0;
      return { ...item, gradings: newGradings, tolPlus: tolPlusValue, tolMinus: tolMinusValue };
    });
  };

  useEffect(() => {
    if (id) {
      const typeLookup = {
        in: 'inch',
        cm: 'cm',
      };
      let newModifiedArray = [];
      if (GradingDataById?.pomLibraries) {
        newModifiedArray = applyToDecimalFunctionality();
      }
      const gradType = UnitOfMeasure?.find((unit) => unit?._id === gradingGroupState?.conversionTypeId);

      // Normalize product type from edit response to array for MultiSelect
      let hydratedPT = [];
      if (Array.isArray(GradingDataById?.productType)) {
        hydratedPT = GradingDataById.productType;
      } else if (Array.isArray(GradingDataById?.productTypeId)) {
        hydratedPT = GradingDataById.productTypeId;
      } else if (Array.isArray(GradingDataById?.productTypeIds)) {
        hydratedPT = GradingDataById.productTypeIds;
      } else if (GradingDataById?.productTypeId) {
        hydratedPT = [GradingDataById.productTypeId];
      }

      // Normalize categories to arrays of IDs (not objects) to keep filtering logic working
      const toIds = (arr) => (Array.isArray(arr) ? arr : [arr]).map((x) => x?._id || x).filter(Boolean);
      const toObjs = (arr, master) => {
        const input = Array.isArray(arr) ? arr : [arr];
        const masterById = new Map((master || []).map((m) => [m?._id, m]));
        return input
          .map((x) => (x && typeof x === 'object' ? x : { _id: x }))
          .map((o) => {
            const m = masterById.get(o?._id);
            return m || o;
          });
      };

      setgradingGroup((prev) => {
        const groupIds = new Set(newModifiedArray.map((item) => item._id));
        const preservedPoms = prev.pomTableData.filter((item) => item._id && !groupIds.has(item._id));
        return {
          ...prev,
          groupName: GradingDataById?.name,
          groupDescription: GradingDataById?.description,
          selectGradingBrand: toIds(GradingDataById?.brandId || []),
          selectCategoryOne: toIds(GradingDataById?.catsOneIds || []),
          selectCategoryTwo: toIds(GradingDataById?.catsTwoIds || []),
          selectCategoryThree: toIds(GradingDataById?.catThreeId || []),
          refBrands: toObjs(GradingDataById?.brandId || [], BrandList),
          refSubBrands: toObjs(GradingDataById?.subBrandId || [], multiSubBrandList),
          refCatsOne: toObjs(GradingDataById?.catsOneIds || [], CategoryOneList),
          refCatsTwo: toObjs(GradingDataById?.catsTwoIds || [], CategoryTwoList),
          refCatThree: toObjs(GradingDataById?.catThreeId || [], CategoryThreeList),
          refProductTypes: toObjs(hydratedPT, pcTypeList),
          pomTableData: [...newModifiedArray, ...preservedPoms],
          sizeRangeId: GradingDataById?.sizeRangeId || null,
          sizeRanges: GradingDataById?.sizeRanges,
          sizeRangesForHeader: GradingDataById?.sizeRanges?.map((item) => ({
            sizeName: item?.name,
            isSampleSize: item?.isSampleSize,
            isZero: !!item?.isSampleSize,
          })),
          sampleSize: GradingDataById?.sizeRanges,
          active: GradingDataById?.isPublished,
          applySizeRangesToHeaderText: true,
          conversationType: gradType?.name || typeLookup[GradingDataById.conversationType],
          conversionTypeId: GradingDataById?.conversionTypeId,
          isAdd: false,
          selectSubBrand: toIds(GradingDataById?.subBrandId || []),
          productTypeId: hydratedPT.length ? hydratedPT : prev.productTypeId,
          editModeLoading: false,
        };
      });

      if (foundReferences) {
        const msg = t('POMS.GRADING_GROUP.GRADING_GROUP_PROVIDER.BOMBYX_PLM_MESSAGE');
        toasterMsg(msg, 'info');
        setgradingGroup((prev) => ({
          ...prev,
          isDelete: false,
        }));
      }
    } else {
      setgradingGroup((prev) => ({
        ...prev,
        isAdd: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GradingDataById, id, foundReferences]);
  useEffect(() => {
    setgradingGroup((prev) => ({
      ...prev,
      CategoryThreeData: createOptions(CategoryThreeList, 'name', '_id'),
    }));
    if (CategoryThreeList?.length === 0) {
      setgradingGroup((prev) => ({
        ...prev,
        selectCategoryThree: [],
      }));
    }
  }, [CategoryThreeList]);

  useEffect(() => {
    const defaultUnit = ProfileData?.unit === 'in' ? 'inch' : 'cm';
    const unitId = UnitOfMeasure?.find((unit) => unit?.name?.toLowerCase() === defaultUnit?.toLowerCase());
    setgradingGroup((prev) => ({
      ...prev,
      BrandData: createOptions(BrandList, 'name', '_id'),
      CategoryOneData: createOptions(CategoryOneList, 'name', '_id'),
      CategoryTwoData: createOptions(CategoryTwoList, 'name', '_id'),
      conversationType: ProfileData?.unit === 'in' ? 'inch' : 'cm',
      conversionTypeId: unitId?._id || '',
    }));
  }, [BrandList, CategoryOneList, CategoryTwoList, ProfileData, UnitOfMeasure]);

  const handleCheckPOMList = useCallback(
    async (isChecked, tempId) => {
      const existingIds = gradingGroupState?.selectedPOM;
      if (isChecked && !existingIds.includes(tempId)) {
        setgradingGroup((prevState) => ({
          ...prevState,
          selectedPOM: [...existingIds, tempId],
        }));
      } else {
        const removedArray = existingIds?.filter((eleId) => eleId !== tempId);

        if (checkArray(removedArray)) {
          setgradingGroup((prevState) => ({
            ...prevState,
            selectedPOM: removedArray,
          }));
        } else {
          setgradingGroup((prevState) => ({
            ...prevState,
            selectedPOM: [],
          }));
        }
      }
    },
    [gradingGroupState?.selectedPOM],
  );

  const isallChecked = checkArray(gradingGroupState?.pomTableData) && gradingGroupState?.selectedPOM?.length === gradingGroupState?.pomTableData?.length;
  const handleCheckAll = useCallback(
    async (e) => {
      if (e.target.checked) {
        const allIds =
          gradingGroupState?.pomTableData &&
          checkArray(gradingGroupState?.pomTableData) &&
          gradingGroupState?.pomTableData.map((ele) => pomRowKey(ele));

        if (allIds) {
          setgradingGroup((prevState) => ({
            ...prevState,
            selectedPOM: allIds.filter(Boolean),
          }));
        }
      } else {
        setgradingGroup((prevState) => ({
          ...prevState,
          selectedPOM: [],
        }));
      }
    },
    [gradingGroupState, setgradingGroup],
  );

  const isRenamedSizeRangeAppliedBeforeSave = () => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < gradingGroupState?.sizeRanges.length; i++) {
      if (gradingGroupState?.sizeRangesForHeader[i]?.sizeName !== gradingGroupState?.sizeRanges[i]?.name) {
        return false;
      }
    }
    return true;
  };

  const issampleSizeAppliedBeforeSave = () => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < gradingGroupState?.sizeRanges.length; i++) {
      if (gradingGroupState?.sizeRangesForHeader[i]?.isSampleSize !== gradingGroupState?.sizeRanges[i]?.isSampleSize) {
        return false;
      }
    }
    return true;
  };

  const isAddedOrRemovedSizeRangeAppliedBeforeSave = () => {
    if (gradingGroupState?.pomTableData?.length > 0) {
      return gradingGroupState?.sizeRanges?.length === gradingGroupState?.sizeRangesForHeader?.length;
    }
    return true;
  };

  const insertIdAfterData = async (res) => {
    if (!id) {
      history.push(`/libraries/gradings/edit/${res?.data?.insertedId}`);
    } else {
      const params = {
        isPubParam: 1,
        loggedInUserId,
      };
      await dispatch(action.fetchGradingGroupById(id, params));
    }
  };
  useEffect(() => {
    const fetchPomData = async () => {
      const params = {
        loggedInUserId,
      };

      // Apply functionality to modify array if GradingDataById exists
      const newModifiedArray = GradingDataById?.pomLibraries ? applyToDecimalFunctionality() : [];

      const selectedPomLibraryIds = JSON.parse(localStorage.getItem('gradingGroupPomIds'));
      if (selectedPomLibraryIds) {
        setgradingGroup((prev) => ({
          ...prev,
          isLoading: true,
        }));
        const res = await dispatch(action.getAllPom(params, t));

        if (res?.data) {
          const tableData = res?.data?.data?.filter((item) => selectedPomLibraryIds.includes(item._id));

          const currentPoms = pomTableDataRef.current?.length ? pomTableDataRef.current : newModifiedArray;
          const matchingObjects = currentPoms.filter((item) => selectedPomLibraryIds.includes(item._id));
          const notMatchingObjects = tableData.filter((item) => !matchingObjects.some((obj) => obj._id === item._id));
          // Display toaster message for matching objects
          if (matchingObjects.length) {
            const pomCodes = matchingObjects.map((matchingObject) => matchingObject.code);
            toasterMsg(`${pomCodes.join(', ')} ${t('POM_GROUP.POM_EXIST')}`, 'danger');
          }

          // Update grading for non-matching objects and update state
          if (notMatchingObjects.length) {
            const addGrading = (GradingDataById?.sizeRanges || []).map((item) => ({
              sizeName: item?.name,
              isSampleSize: item?.isSampleSize,
              isZero: !!item?.isSampleSize,
              grading: 0,
            }));
            const updatedPomLibraries = notMatchingObjects.map((library) => ({
              ...library,
              gradings: addGrading,
              tolPlus: 0,
              tolMinus: 0,
            }));
            setgradingGroup((prev) => ({
              ...prev,
              pomTableData: [...prev.pomTableData, ...updatedPomLibraries],
            }));
          }
          setgradingGroup((prev) => ({ ...prev, isLoading: false }));
        }
      }

      // Clean up
      localStorage.removeItem('gradingGroupPomIds');
    };

    if (id && GradingDataById?.sizeRanges?.length) {
      fetchPomData();
    }
  }, [GradingDataById, id]);

  const UpdateGradingGroup = useCallback(
    async (updatedPomsFromrearrange) => {
      const gradingGroupData = updatedPomsFromrearrange?.length ? updatedPomsFromrearrange : gradingGroupState?.pomTableData;
      const isReArrange = !!(id && updatedPomsFromrearrange?.length);
      const isLengthMatched = isAddedOrRemovedSizeRangeAppliedBeforeSave();
      const isValid = isRenamedSizeRangeAppliedBeforeSave();
      const checkSampleSizeValid = issampleSizeAppliedBeforeSave();

      if (isLengthMatched && isValid && checkSampleSizeValid) {
        const updatedPomTable = gradingGroupData.map((item) => {
          let updatedGrading = [];
          if (item?.gradings?.length > 0) {
            updatedGrading = item?.gradings?.map((gradingItem) => {
              const sizeFromHeader = gradingGroupState?.sizeRangesForHeader?.find((headerItem) => headerItem.sizeName === gradingItem.sizeName);

              const isSampleSize = gradingItem.isSampleSize !== undefined ? gradingItem.isSampleSize : sizeFromHeader?.isSampleSize || false;

              if (isSampleSize === true || !gradingItem.grading) {
                return { ...gradingItem, isSampleSize, grading: '0' };
              }
              return { ...gradingItem, isSampleSize };
            });
          } else {
            updatedGrading = [];
            updatedGrading = gradingGroupState?.sizeRangesForHeader?.map((item) => ({
              ...item,
              grading: 0,
            }));
          }

          return { ...item, gradings: updatedGrading };
        });
        const gradType = UnitOfMeasure?.find((unit) => unit?._id === gradingGroupState?.conversionTypeId);
        const req = {
          brandId: gradingGroupState?.selectGradingBrand,
          catsOneIds: gradingGroupState?.selectCategoryOne,
          catsTwoIds: gradingGroupState?.selectCategoryTwo,
          catThreeId: gradingGroupState?.selectCategoryThree,
          description: gradingGroupState?.groupDescription,
          name: gradingGroupState?.groupName,
          isPublished: gradingGroupState?.active,
          loggedInUserId,
          pomLibraries: JSON.stringify(updatedPomTable),
          sizeRanges: JSON.stringify(gradingGroupState?.sizeRanges),
          conversationType: gradType?.name,
          conversionTypeId: gradingGroupState?.conversionTypeId,
          fraction: 'off',
          gradingLibraryId: '',
          subBrandId: gradingGroupState?.selectSubBrand || [],
        };
        req.sizeRangeId = gradingGroupState?.sizeRangeId || null;
        if (gradingGroupState?.catBasedOnPTData) {
          req.productType = gradingGroupState?.productTypeId;
        }
        if (id) {
          req.gradingLibraryId = id;
        }
        const res = await dispatch(action.saveGradingGroup(req, t, isReArrange));
        if (res?.code === 200) {
          await insertIdAfterData(res);
        }
      } else {
        setgradingGroup((errState) => ({
          ...errState,
          showApplyConfirm: true,
        }));
      }
    },
    [gradingGroupState],
  );

  /**
   * Normalises a Brand / Sub Brand / Category selection to a sorted list of unique id strings so two
   * selections compare equal regardless of ordering, duplicates, or whether the control stores raw
   * ids or option objects.
   * @param {*} value - Selection held in state or loaded from the saved grading group.
   * @returns {Array<string>} Sorted unique id strings.
   */
  const toComparableIds = (value) => {
    if (value === null || value === undefined) return [];
    const list = Array.isArray(value) ? value : [value];
    const ids = list
      .map((entry) => (entry && typeof entry === 'object' ? entry._id ?? entry.id : entry))
      .filter((entry) => entry !== null && entry !== undefined && entry !== '')
      .map((entry) => String(entry));
    return [...new Set(ids)].sort();
  };

  /**
   * Reports whether two Brand / Sub Brand / Category selections hold the same ids.
   * @param {*} left - First selection.
   * @param {*} right - Second selection.
   * @returns {boolean} True when both hold the same id set.
   */
  const isSameIdSet = (left, right) => {
    const leftIds = toComparableIds(left);
    const rightIds = toComparableIds(right);
    return leftIds.length === rightIds.length && leftIds.every((entry, index) => entry === rightIds[index]);
  };

  /**
   * Reports whether the edit changed the group's Brand / Sub Brand / Category scope. Only a change
   * here can strand a product on a group that no longer covers it, so a rename, a POM edit, or a
   * size-range change saves without warning. Add mode has no saved scope to compare against.
   * @returns {boolean} True when the scope differs from the saved grading group.
   */
  const hasGradingScopeChanged = useCallback(() => {
    if (!id || !GradingDataById) return false;
    return (
      !isSameIdSet(gradingGroupState?.selectGradingBrand, GradingDataById?.brandId) ||
      !isSameIdSet(gradingGroupState?.selectSubBrand, GradingDataById?.subBrandId) ||
      !isSameIdSet(gradingGroupState?.selectCategoryOne, GradingDataById?.catsOneIds) ||
      !isSameIdSet(gradingGroupState?.selectCategoryTwo, GradingDataById?.catsTwoIds) ||
      !isSameIdSet(gradingGroupState?.selectCategoryThree, GradingDataById?.catThreeId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, GradingDataById, gradingGroupState]);

  /**
   * Reports whether this save must stop and ask the user to confirm the scope change first. A save
   * the user has already confirmed goes straight through, so the popup is shown once per save.
   * @param {boolean} isScopeConfirmed - Whether the user already accepted the warning for this save.
   * @returns {boolean} True when the save must be held for confirmation.
   */
  const shouldHoldForScopeConfirmation = useCallback(
    (isScopeConfirmed) => !isScopeConfirmed && hasGradingScopeChanged(),
    [hasGradingScopeChanged],
  );

  /**
   * Reports whether the grading group satisfies every check that must hold before it can be saved.
   * The individual failures have already been surfaced as toasts by the caller; this only decides
   * whether the save may proceed.
   * @param {Object} checks - Results already computed by the caller.
   * @param {Array} checks.validSizeRange - Size ranges left without a name.
   * @param {Array} checks.arrSampleSizeNotSet - Size ranges not marked as the sample size.
   * @param {boolean} checks.isDuplicate - Whether two size ranges share a name.
   * @param {boolean} checks.hasDuplicatePomCodes - Whether two POM rows share a code.
   * @param {boolean} checks.isMissingProductType - Whether a company scoping categories by Product
   *   Type left the Product Type unset.
   * @returns {boolean} True when the grading group may be saved.
   */
  const isGradingGroupReadyToSave = ({ validSizeRange, arrSampleSizeNotSet, isDuplicate, hasDuplicatePomCodes }) => {
    /**
     * Product Type is required, and the form already reports it as such, so the save has to test it
     * here too. It was referenced as a bare `isMissingProductType` that nothing declared, which threw
     * a ReferenceError out of this guard before any check could return — every grading group save,
     * add and edit alike, died on that throw with no request sent and no message shown.
     *
     * The check is gated on `catBasedOnPTData` because the field itself is: the whole Product Type
     * column is only rendered under that flag, so demanding it while the flag is off would make the
     * form permanently unsavable on a tenant that never shows the field. The condition is otherwise
     * the one the field's own error message uses, so the two cannot disagree.
     */
    const isMissingProductType = Boolean(gradingGroupState?.catBasedOnPTData) && !gradingGroupState?.productTypeId?.length;

    return (
      !isEmpty(gradingGroupState.groupName) &&
      !isEmpty(gradingGroupState.groupDescription) &&
      !isEmpty(gradingGroupState.selectCategoryTwo) &&
      !isEmpty(gradingGroupState.selectCategoryOne) &&
      !validSizeRange?.length &&
      gradingGroupState?.sizeRanges.length !== arrSampleSizeNotSet.length &&
      !isDuplicate &&
      !hasDuplicatePomCodes &&
      !isMissingProductType
    );
  };

  const handleSubmitData = useCallback(
    async (updatedPomsFromrearrange, isScopeConfirmed = false) => {
      const validSizeRange = gradingGroupState?.sizeRanges.filter((item) => !item.name);
      if (validSizeRange.length) {
        toasterMsg(t('POMS.GRADING_GROUP.GRADING_GROUP_PROVIDER.SIZE_RANGE_REQUIRED'), 'danger');
      }
      const valueArr = gradingGroupState?.sizeRanges?.map((item) => item?.name);

      const isDuplicate = valueArr?.some((item, idx) => valueArr?.indexOf(item) !== idx);
      if (isDuplicate) {
        toasterMsg(t('POMS.GRADING_GROUP.GRADING_GROUP_PROVIDER.PLEASE_UNIQUE_SIZE'), 'danger');
      }
      const arrSampleSizeNotSet = gradingGroupState?.sizeRanges?.filter((value) => !value.isSampleSize);

      if (gradingGroupState?.sizeRanges.length === arrSampleSizeNotSet.length) {
        toasterMsg(t('POMS.GRADING_GROUP.GRADING_GROUP_PROVIDER.PLEASE_SELECT_SAMPLE_SIZE'), 'danger');
      }

      const pomCodes = (gradingGroupState?.pomTableData || []).map((item) => item?.code).filter(Boolean);
      const duplicatePomCodes = pomCodes.filter((code, idx) => pomCodes.indexOf(code) !== idx);
      const hasDuplicatePomCodes = duplicatePomCodes.length > 0;
      if (hasDuplicatePomCodes) {
        const uniqueDuplicates = [...new Set(duplicatePomCodes)].join(', ');
        toasterMsg(t('POMS.GRADING_GROUP.GRADING_GROUP_PROVIDER.DUPLICATE_POM_CODE', { codes: uniqueDuplicates }), 'danger');
      }

      if (isGradingGroupReadyToSave({ validSizeRange, arrSampleSizeNotSet, isDuplicate, hasDuplicatePomCodes })) {
        /*
         * Changing the scope can drop the group off products it no longer covers, taking their
         * seeded POM and specification data with it, so the save is held until the user confirms.
         * The pending re-arrange payload is kept only when it is a real POM array — the Save button
         * hands this callback a click event, which must not be replayed as one.
         */
        if (shouldHoldForScopeConfirmation(isScopeConfirmed)) {
          setgradingGroup((prevState) => ({
            ...prevState,
            isLoading: false,
            showGradingScopeConfirm: true,
            pendingScopeSubmitPoms: Array.isArray(updatedPomsFromrearrange) ? updatedPomsFromrearrange : null,
          }));
          return;
        }

        setgradingGroup((prevState) => ({
          ...prevState,
          isLoading: true,
        }));

        try {
          await UpdateGradingGroup(updatedPomsFromrearrange);
        } catch (error) {
          toasterMsg(t('POMS.GRADING_GROUP.GRADING_GROUP_PROVIDER.PLEASE_TRY_AGAIN'), 'danger');
        } finally {
          setgradingGroup((prevState) => ({
            ...prevState,
            isLoading: false,
          }));
        }
      } else {
        setgradingGroup((errState) => ({
          ...errState,
          isLoading: false,
          showError: true,
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gradingGroupState, UpdateGradingGroup, shouldHoldForScopeConfirmation],
  );

  /**
   * Proceeds with the held save once the user accepts that products the new scope no longer covers
   * will lose this group and its measurement data.
   */
  const confirmGradingScopeChange = useCallback(() => {
    const pendingPoms = gradingGroupState?.pendingScopeSubmitPoms;
    setgradingGroup((prevState) => ({
      ...prevState,
      showGradingScopeConfirm: false,
      pendingScopeSubmitPoms: null,
    }));
    handleSubmitData(pendingPoms, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradingGroupState?.pendingScopeSubmitPoms, handleSubmitData]);

  /** Dismisses the scope-change warning and abandons the held save, leaving the edits on screen. */
  const cancelGradingScopeChange = useCallback(() => {
    setgradingGroup((prevState) => ({
      ...prevState,
      showGradingScopeConfirm: false,
      pendingScopeSubmitPoms: null,
    }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateReArrangeSequence = useCallback(
    (array) => {
      setgradingGroup((prev) => ({
        ...prev,
        pomTableData: array,
      }));
      if (id) {
        handleSubmitData(array);
      }
    },
    [id, handleSubmitData],
  );

  /**
   * Permanently removes the given POM rows from this grading group.
   *
   * Persists straight away through the dedicated $pull endpoint rather than
   * waiting for Save, so a deleted POM cannot reappear on the next visit and
   * can be re-imported immediately. POMs added on screen but never saved have
   * no stored row, so they are dropped locally without an API call. On failure
   * the table is restored, keeping it in step with the database.
   *
   * A stored row carrying no `_id` cannot be addressed by the endpoint at all, so the whole
   * batch is refused with a message rather than half-applied — removing it from the table
   * while the database kept it is what made deleting one look like it silently did nothing.
   */
  const removePomsFromGroup = useCallback(
    async (pomIds) => {
      const keys = (Array.isArray(pomIds) ? pomIds : [pomIds]).filter(Boolean);
      if (!keys.length) {
        return;
      }

      const previousPoms = pomTableDataRef.current || [];
      /*
       * Rows are selected by `pomRowKey`, which falls back to `code` for stored rows that carry
       * no `_id`. Only a real id can be sent to the delete endpoint, which rejects anything that
       * is not an ObjectId — so an unaddressable row is reported rather than silently skipped,
       * which is how deleting one used to do nothing at all.
       */
      const { deletableIds, unresolved } = resolvePomDeletion(keys, GradingDataById?.pomLibraries);

      if (unresolved.length) {
        toasterMsg(t('POMS.GRADING_GROUP.GRADING_GROUP_PROVIDER.POM_ROW_NOT_DELETABLE', { codes: unresolved.join(', ') }), 'danger');
        return;
      }

      setgradingGroup((prev) => ({
        ...prev,
        pomTableData: prev.pomTableData.filter((pom) => !keys.includes(pomRowKey(pom))),
        selectedPOM: prev.selectedPOM.filter((pomKey) => !keys.includes(pomKey)),
      }));

      if (!id || !deletableIds.length) {
        return;
      }

      const res = await dispatch(action.deleteGradingGroupPoms(id, deletableIds, t));
      if (res?.code !== 200) {
        setgradingGroup((prev) => ({
          ...prev,
          pomTableData: previousPoms,
        }));
      }
    },
    [dispatch, id, t, GradingDataById],
  );
  useEffect(() => {
    return () => {
      dispatch(action.clearGradingGroupReducer());
      setgradingGroup((prev) => ({
        ...prev,
        pomTableData: [],
        selectedPOM: [],
      }));
    };
  }, [dispatch]);

  const memoizedContextValue = useMemo(() => {
    return {
      gradingGroupState,
      setgradingGroup,
      loggedInUserId,
      pomList,
      handleCheckPOMList,
      handleCheckAll,
      isallChecked,
      handleSubmitData,
      UnitOfMeasure,
      ecommAcess,
      eCommSpecData,
      foundReferences,
      GradingAddEditAccess,
      GradingDeleteAccess,
      isView,
      id,
      updateReArrangeSequence,
      removePomsFromGroup,
      confirmGradingScopeChange,
      cancelGradingScopeChange,
    };
  }, [
    gradingGroupState,
    setgradingGroup,
    loggedInUserId,
    pomList,
    handleCheckPOMList,
    handleCheckAll,
    isallChecked,
    handleSubmitData,
    UnitOfMeasure,
    ecommAcess,
    eCommSpecData,
    foundReferences,
    GradingAddEditAccess,
    GradingDeleteAccess,
    isView,
    id,
    updateReArrangeSequence,
    removePomsFromGroup,
    confirmGradingScopeChange,
    cancelGradingScopeChange,
  ]);

  return <StateContext.Provider value={memoizedContextValue}>{children}</StateContext.Provider>;
}

export function useGradingGroupState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the StateProvider');
  }
  return context;
}
